import {noop} from 'lodash';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface TrialEndedBannerProps {
  isGlobalAdmin: boolean;
  daysLeftInTrial: number;
  goToBilling: () => void;
  contactUs: () => void;
  composeUpgradePlanMessageToAdmin: () => void;
}

/*
 * Style.
 */

const StyledDescriptionDiv = styled.div`
  padding-top: 4px;
  font-size: 12px;
`;

const StyledBanner = styled(Banner)`
  grid-template-areas:
    'icon content action'
    '. content .'
    '. footer footer';
`;

/*
 * Component.
 */

export const TrialEndedBanner: FC<TrialEndedBannerProps> = (props) => {
  const {isGlobalAdmin, daysLeftInTrial, goToBilling, contactUs, composeUpgradePlanMessageToAdmin} = props;

  const isBannerDismissible = daysLeftInTrial > 0;
  const [isBannerDismissed, makeHandlerWithDismissal] = useDismissal(isBannerDismissible);
  const theme = useUserTheme();

  if (isBannerDismissed) {
    return null;
  }

  const onCloseClick = makeHandlerWithDismissal(noop);
  const onUpgradeClick = makeHandlerWithDismissal(() => {
    goToBilling();
    trackInterfaceEvent(InterfaceEventsEnum.TRIAL_UPGRADE_CLICK, {
      location: 'banner',
    });
  });
  const onContactAdminClick = makeHandlerWithDismissal(() => {
    composeUpgradePlanMessageToAdmin();
  });
  const onContactFrontClick = makeHandlerWithDismissal(() => {
    contactUs();
  });

  const {color, priority} = computeBannerProps(daysLeftInTrial, theme);

  const renderButtons = (): ReactNode => {
    if (!isGlobalAdmin) {
      return (
        <BannerAction onClick={onContactAdminClick}>
          <Trans id="banner-trialending-action-contactadmin">Contact administrator</Trans>
        </BannerAction>
      );
    }

    return (
      <>
        <BannerAction onClick={onUpgradeClick}>
          <Trans id="banner-trialending-action-upgradeplan">See plans</Trans>
        </BannerAction>

        <BannerAction onClick={onContactFrontClick}>
          <Trans id="banner-trialending-action-contact-us">Contact us</Trans>
        </BannerAction>
      </>
    );
  };

  return (
    <StyledBanner bannerType={BannerTypesEnum.MULTILINE} color={color} priority={priority}>
      {isBannerDismissible && <BannerCloseAction onClick={onCloseClick} />}
      <BannerIcon iconName="warningFilledSmall" />
      <BannerContent>
        <BannerContentTitle>{renderDaysLeftInTrial(daysLeftInTrial)}</BannerContentTitle>
        <StyledDescriptionDiv>{renderDescription(daysLeftInTrial, isGlobalAdmin)}</StyledDescriptionDiv>
      </BannerContent>
      <BannerFooterActions>{renderButtons()}</BannerFooterActions>
    </StyledBanner>
  );
};

/*
 * Hooks.
 */

function useDismissal(isDismissible: boolean) {
  const [isDismissed, setIsDismissed] = useState(false);

  /** Creates an event handler that dismisses the banner, if it is dismissible. */
  const makeHandlerWithDismissal =
    (handler: () => void): (() => void) =>
    () => {
      if (isDismissible) {
        setIsDismissed(true);
      }

      handler();
    };

  return [isDismissed, makeHandlerWithDismissal] as const;
}

/*
 * Helpers.
 */

function computeBannerProps(daysLeftInTrial: number, theme: UserTheme) {
  if (daysLeftInTrial === 0) {
    return {
      color: theme.palette.orange.shade40,
      priority: BannerPrioritiesEnum.ERROR,
    };
  }

  return {
    color: theme.palette.orange.shade40,
    priority: BannerPrioritiesEnum.INFORMATION,
  };
}

function renderDaysLeftInTrial(daysLeftInTrial: number) {
  return (
    <Plural
      id="banner-trialend-title-daysleftintrial"
      value={daysLeftInTrial}
      _0="Your free trial has ended"
      one="Your trial ends today"
      other="Your trial ends in # days"
    />
  );
}

function renderDescription(daysLeftInTrial: number, isGlobalAdmin: boolean) {
  if (daysLeftInTrial === 0 && !isGlobalAdmin) {
    return (
      <Trans id="banner-trialending-description-endednonadmin">
        Please contact your admin to upgrade your plan to keep using Front.
      </Trans>
    );
  }

  if (!isGlobalAdmin) {
    return (
      <Trans id="banner-trialending-description-endingsoonnonadmin">
        Please contact your admin to upgrade your plan to avoid service disruption.
      </Trans>
    );
  }

  return (
    <Trans id="banner-trialending-description-admin">
      Please upgrade your plan, or contact us if you need help or have any questions.
    </Trans>
  );
}
